import { Box, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { FONT_SIZE, HOLIDAY_GROUPS, INK } from "../../../theme/tokens";
import { JumpingDots } from "../../UIToolkit/Animations/JumpingDots";
import { DateInput } from "../../UIToolkit/Inputs/DateInput";
import { type DropdownItem, SystemDropdown } from "../../UIToolkit/Inputs/SystemDropDown";
import { InlineHint } from "../../UIToolkit/molecules/InlineHint";
import SystemDialog from "../../UIToolkit/Popups/Dialog";
import { HolidayNameField } from "./HolidayNameField";
import { HolidaySectionError } from "./HolidaySectionError";
import { HolidayShiftFields } from "./HolidayShiftFields";
import { type HolidayFirstPart, type HolidayLastPart, NEW_HOLIDAY_FIRST_PART, NEW_HOLIDAY_LAST_PART } from "./holidayApi";
import { formatHolidayDate, holidayNameLooksLikeDate } from "./holidayFormat";
import { type HolidayPreset, upcomingHolidayPresets } from "./holidayPresets";
import { useCreateHoliday, useDepartmentToday, useUpcomingHolidays } from "./holidayQueries";

const CUSTOM = "custom";
const ISO_DATE = "yyyy-MM-dd";
const presetKey = (preset: HolidayPreset) => `${preset.name}|${preset.date.toFormat(ISO_DATE)}`;

type Props = { onClose: () => void };

/** "Add holiday": a common holiday on its next date, or a custom one. The group is chosen later, in the list. */
export const HolidayAddDialog: FC<Props> = ({ onClose }) => {
	const { t, i18n } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { chosenDepartment } = useHospitalData();
	const today = useDepartmentToday();
	const upcoming = useUpcomingHolidays(chosenDepartment.department_id);
	const language = i18n.language;
	// Undefined until the holidays already added have loaded — they decide which common holidays are left.
	const presets = useMemo(
		() => (upcoming.data === undefined ? undefined : upcomingHolidayPresets(today, upcoming.data, language)),
		[today, upcoming.data, language],
	);
	const [pick, setPick] = useState<string | null>(null);
	// Until the scheduler picks, the first common holiday is chosen — or Custom when none is left to offer.
	const effectivePick = pick ?? (presets !== undefined && presets.length > 0 ? presetKey(presets[0]) : CUSTOM);
	const [customName, setCustomName] = useState("");
	const [customDate, setCustomDate] = useState<DateTime | undefined>(undefined);
	const [firstPart, setFirstPart] = useState<HolidayFirstPart>(NEW_HOLIDAY_FIRST_PART);
	const [lastPart, setLastPart] = useState<HolidayLastPart>(NEW_HOLIDAY_LAST_PART);
	const [missingNameOrDate, setMissingNameOrDate] = useState(false);
	const create = useCreateHoliday();

	const items = useMemo<DropdownItem<string>[]>(
		() => [
			{ value: CUSTOM, label: t("holidays.dialog.custom") },
			// DEFAULT: never read while loading — the form isn't shown until the common holidays are known.
			...(presets ?? []).map((preset) => ({
				value: presetKey(preset),
				label: t("holidays.dialog.presetOption", { name: preset.name, date: formatHolidayDate(preset.date) }),
			})),
		],
		[presets, t],
	);
	const preset = presets?.find((candidate) => presetKey(candidate) === effectivePick);
	const isCustom = preset === undefined;
	const nameLooksLikeDate = isCustom && holidayNameLooksLikeDate(customName);

	const handleConfirm = () => {
		const name = isCustom ? customName.trim() : preset.name;
		const date = isCustom ? customDate : preset.date;
		if (nameLooksLikeDate) return;
		if (name === "" || date === undefined) {
			setMissingNameOrDate(true);
			return;
		}
		create.mutate(
			{ name, date: date.toFormat(ISO_DATE), firstPart, lastPart, selectedGroupId: null },
			{
				onSuccess: (created) => {
					showSnackbar(t("holidays.dialog.added", { name: created.name }), "success");
					onClose();
				},
			},
		);
	};

	const form = (
		<>
			<SystemDropdown<DropdownItem<string>>
				items={items}
				selectedItem={effectivePick}
				onChange={(next) => {
					if (next === undefined) return;
					setPick(next);
					setMissingNameOrDate(false);
				}}
				label={t("holidays.dialog.holiday")}
				fullWidth
				menuPlacement="bottom-start"
				data-testid="holiday-dialog-preset"
			/>
			{isCustom && (
				<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
					<HolidayNameField
						value={customName}
						onChange={(name) => {
							setCustomName(name);
							setMissingNameOrDate(false);
						}}
						placeholder={t("holidays.dialog.namePlaceholder")}
						autoFocus
						dataTestId="holiday-dialog-custom-name"
					/>
					<DateInput
						label={t("date")}
						value={customDate}
						onChange={(date) => {
							setCustomDate(date.startOf("day"));
							setMissingNameOrDate(false);
						}}
						minDate={today}
						formControlSx={{ width: "100%" }}
						dataTestId="holiday-dialog-custom-date"
					/>
				</Box>
			)}
			{missingNameOrDate && (
				<InlineHint tone="warning" dataTestId="holiday-dialog-missing">
					{t("holidays.dialog.missingNameDate")}
				</InlineHint>
			)}
			<HolidayShiftFields firstPart={firstPart} lastPart={lastPart} onFirstPartChange={setFirstPart} onLastPartChange={setLastPart} />
			<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>{t("holidays.dialog.groupLater")}</Typography>
		</>
	);

	// The form waits for the common holidays, so the pick can't jump from Custom to one of them mid-typing.
	const renderBody = () => {
		if (upcoming.isError) return <HolidaySectionError onRetry={() => upcoming.refetch()} dataTestId="holiday-add-error" />;
		if (presets === undefined) {
			return (
				<Box sx={{ display: "flex", justifyContent: "center", py: 4 }} data-testid="holiday-add-loading">
					<JumpingDots size={6} />
				</Box>
			);
		}
		return form;
	};

	const body = (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1, width: { sm: HOLIDAY_GROUPS.FORM_DIALOG_BODY_WIDTH_PX } }}>{renderBody()}</Box>
	);

	return (
		<SystemDialog
			open
			onClose={onClose}
			title={t("holidays.dialog.addTitle")}
			body={body}
			cancel={t("cancel")}
			confirm={t("holidays.add")}
			confirmButtonVariant="primary"
			onConfirm={handleConfirm}
			confirmDisabled={presets === undefined || nameLooksLikeDate}
			confirmLoading={create.isPending}
			dataTestId="holiday-add-dialog"
			confirmDataTestId="holiday-add-confirm"
			cancelDataTestId="holiday-add-cancel"
		/>
	);
};
